import React, { useState } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2, X, Search, ChevronDown, ChevronUp, DollarSign } from 'lucide-react';
import { useProductos, invalidateProductosCache } from '../utils/useProductos';

const FORM_VACIO = {
  codigo: '', marca: '', producto: '', presentacion: '', precio: '', descripcion: '',
  litros: '', densidad: '', peso_kg: '', solidos_pct: '', thinner: '', caracteristicas: '',
};

export default function PreciosPage() {
  const { productos, loading } = useProductos();
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null); // { editing: producto|null }
  const [form, setForm] = useState(FORM_VACIO);
  const [showAvanzado, setShowAvanzado] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lista, setLista] = useState(null); // lista propia, se resincroniza con el cache tras cada cambio

  const productosMostrar = lista ?? productos;

  const recargar = async () => {
    invalidateProductosCache();
    try {
      const res = await api.get('/products');
      setLista(res.data);
    } catch { /* noop */ }
  };

  const prodFilt = productosMostrar.filter(p =>
    !search ||
    p.codigo?.toLowerCase().includes(search.toLowerCase()) ||
    p.producto?.toLowerCase().includes(search.toLowerCase()) ||
    p.marca?.toLowerCase().includes(search.toLowerCase())
  );

  const abrirNuevo = () => {
    setForm(FORM_VACIO);
    setShowAvanzado(false);
    setModal({ editing: null });
  };

  const abrirEditar = (p) => {
    setForm({
      codigo: p.codigo || '', marca: p.marca || '', producto: p.producto || '',
      presentacion: p.presentacion || '', precio: p.precio ?? '', descripcion: p.descripcion || '',
      litros: p.litros ?? '', densidad: p.densidad ?? '', peso_kg: p.peso_kg ?? '',
      solidos_pct: p.solidos_pct ?? '', thinner: p.thinner ?? '',
      caracteristicas: Array.isArray(p.caracteristicas) ? p.caracteristicas.join('\n') : '',
    });
    setShowAvanzado(false);
    setModal({ editing: p });
  };

  const f = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

  const handleGuardar = async () => {
    if (!form.codigo.trim() || !form.producto.trim()) {
      toast.error('Codigo y nombre del producto son obligatorios');
      return;
    }
    setSaving(true);
    const payload = {
      codigo: form.codigo.trim(), marca: form.marca.trim(), producto: form.producto.trim(),
      presentacion: form.presentacion.trim(), precio: form.precio === '' ? null : form.precio,
      descripcion: form.descripcion.trim(),
      litros: form.litros === '' ? null : form.litros,
      densidad: form.densidad === '' ? null : form.densidad,
      peso_kg: form.peso_kg === '' ? null : form.peso_kg,
      solidos_pct: form.solidos_pct === '' ? null : form.solidos_pct,
      thinner: form.thinner === '' ? null : form.thinner,
      caracteristicas: form.caracteristicas.split('\n').map(s => s.trim()).filter(Boolean),
    };
    try {
      if (modal.editing) {
        await api.put(`/products/${modal.editing.id}`, payload);
        toast.success('Producto actualizado');
      } else {
        await api.post('/products', payload);
        toast.success('Producto agregado');
      }
      setModal(null);
      await recargar();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al guardar el producto');
    } finally {
      setSaving(false);
    }
  };

  const handleEliminar = async (p) => {
    if (!window.confirm(`Eliminar "${p.producto}" (${p.codigo})? Esto no afecta cotizaciones ya creadas.`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success('Producto eliminado');
      await recargar();
    } catch { toast.error('Error al eliminar'); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Precios</h1>
          <p className="page-subtitle">Catalogo de productos y precios que usa todo el sistema (Calculadora, Cotizaciones, Hojas tecnicas)</p>
        </div>
        <button className="btn btn-primary" onClick={abrirNuevo}><Plus size={16} /> Agregar producto</button>
      </div>

      <div className="card">
        <div style={{ position: 'relative', marginBottom: 16 }}>
          <Search size={15} style={{ position: 'absolute', left: 10, top: 12, color: '#aaa' }} />
          <input className="form-input" style={{ paddingLeft: 32 }}
            placeholder="Buscar producto por codigo, nombre o marca..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner spinner-dark" style={{ width: 30, height: 30, margin: '0 auto' }} /></div>
        ) : prodFilt.length === 0 ? (
          <div className="empty-state">
            <DollarSign size={40} />
            <h3>Sin productos</h3>
            <p>{search ? `Ningun producto coincide con "${search}"` : 'Agrega el primer producto al catalogo'}</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '2px solid #e5e5e5', color: '#888' }}>
                  <th style={{ padding: '8px 10px' }}>Codigo</th>
                  <th style={{ padding: '8px 10px' }}>Producto</th>
                  <th style={{ padding: '8px 10px' }}>Marca</th>
                  <th style={{ padding: '8px 10px' }}>Presentacion</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right' }}>Precio</th>
                  <th style={{ padding: '8px 10px', width: 90 }}></th>
                </tr>
              </thead>
              <tbody>
                {prodFilt.map(p => (
                  <tr key={p.id || p.codigo} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '8px 10px', fontWeight: 700, color: '#7B0D0D', fontSize: 11 }}>{p.codigo}</td>
                    <td style={{ padding: '8px 10px', fontWeight: 600 }}>{p.producto}</td>
                    <td style={{ padding: '8px 10px', color: '#888' }}>{p.marca}</td>
                    <td style={{ padding: '8px 10px', color: '#888' }}>{p.presentacion}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>
                      {p.precio != null && p.precio !== '' ? `$${parseFloat(p.precio).toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : <span style={{ color: '#ccc' }}>Sin precio</span>}
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button className="btn-icon" title="Editar" onClick={() => abrirEditar(p)}><Edit2 size={14} /></button>
                        <button className="btn-icon" title="Eliminar" onClick={() => handleEliminar(p)} style={{ color: '#dc2626' }}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <div className="modal-overlay">
          <div className="modal modal-lg">
            <div className="modal-header">
              <h3 className="modal-title">{modal.editing ? 'Editar producto' : 'Agregar producto'}</h3>
              <button className="btn-icon" onClick={() => setModal(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Codigo *</label>
                  <input className="form-input" value={form.codigo} onChange={f('codigo')} placeholder="Ej. REV-HEG-001" />
                </div>
                <div className="form-group">
                  <label className="form-label">Marca</label>
                  <input className="form-input" value={form.marca} onChange={f('marca')} placeholder="Ej. HEGGEL" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Nombre del producto *</label>
                <input className="form-input" value={form.producto} onChange={f('producto')} placeholder="Ej. COAT 111" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Presentacion</label>
                  <input className="form-input" value={form.presentacion} onChange={f('presentacion')} placeholder="Ej. CANECA DE 16 KG" />
                </div>
                <div className="form-group">
                  <label className="form-label">Precio (USD)</label>
                  <input className="form-input" type="number" step="0.01" value={form.precio} onChange={f('precio')} placeholder="0.00" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Descripcion</label>
                <textarea className="form-textarea" rows={3} value={form.descripcion} onChange={f('descripcion')} placeholder="Descripcion tecnica breve..." />
              </div>

              <button type="button" onClick={() => setShowAvanzado(!showAvanzado)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: '#0369a1', fontSize: 13, fontWeight: 600, padding: '6px 0', marginBottom: showAvanzado ? 10 : 0 }}>
                {showAvanzado ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                Datos tecnicos avanzados (para la Calculadora de rendimiento)
              </button>

              {showAvanzado && (
                <div style={{ padding: 12, background: '#fafafa', borderRadius: 8, border: '1px solid #e5e5e5' }}>
                  <p style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
                    Opcional — si se dejan vacios, la Calculadora usa valores por defecto (100% solidos, 0% thinner, 1 litro por presentacion).
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Litros por presentacion</label>
                      <input className="form-input" value={form.litros} onChange={f('litros')} placeholder="Ej. 16" />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Densidad</label>
                      <input className="form-input" value={form.densidad} onChange={f('densidad')} placeholder="Ej. 1.8" />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Peso (kg)</label>
                      <input className="form-input" value={form.peso_kg} onChange={f('peso_kg')} placeholder="Ej. 28.8" />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Solidos (fraccion, 1 = 100%)</label>
                      <input className="form-input" value={form.solidos_pct} onChange={f('solidos_pct')} placeholder="Ej. 1" />
                    </div>
                    <div className="form-group" style={{ margin: 0, gridColumn: 'span 2' }}>
                      <label className="form-label">Thinner (fraccion, 0.15 = 15%)</label>
                      <input className="form-input" value={form.thinner} onChange={f('thinner')} placeholder="Ej. 0.15" />
                    </div>
                  </div>
                  <div className="form-group" style={{ marginTop: 12, marginBottom: 0 }}>
                    <label className="form-label">Caracteristicas (una por linea)</label>
                    <textarea className="form-textarea" rows={4} value={form.caracteristicas} onChange={f('caracteristicas')}
                      placeholder={'Volumen de solidos: 100 %\nResistencia a la abrasion (ASTM D4060)...'} />
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleGuardar} disabled={saving}>
                {saving ? 'Guardando...' : modal.editing ? 'Guardar cambios' : 'Agregar producto'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
